import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Crown, Loader2, ArrowRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { login, me } from "@/lib/auth";

export default function AdminLogin() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    me()
      .then(() => navigate("/admin", { replace: true }))
      .catch(() => setChecking(false));
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email, password);
      toast.success("Signed in.");
      navigate("/admin");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid email or password.");
    } finally {
      setBusy(false);
    }
  };

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-6 h-6 text-[#D4AF37] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-5 py-12">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-3 justify-center mb-10">
          <Crown className="w-5 h-5 text-[#D4AF37]" strokeWidth={1.5} />
          <span className="font-serif-display text-lg">
            Crowned Canvas <span className="text-[#D4AF37]">Creations</span>
          </span>
        </Link>
        <div className="border border-white/10 bg-black/40 p-8 md:p-10">
          <p className="overline text-[#D4AF37]">Studio Admin</p>
          <h1 className="font-serif-display text-3xl md:text-4xl leading-tight mt-3">
            Sign in.
          </h1>
          <p className="text-white/55 mt-3 text-sm font-light">
            For studio staff only. Authorized personnel.
          </p>
          <form data-testid="admin-login-form" onSubmit={handleSubmit} className="mt-8 space-y-5">
            <div className="space-y-2.5">
              <Label className="overline text-white/60">Email</Label>
              <Input
                required
                type="email"
                data-testid="admin-email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="bg-black border-white/15 h-12 rounded-none text-white focus-visible:ring-1 focus-visible:ring-[#D4AF37] focus-visible:border-[#D4AF37]"
                placeholder="admin@crownedcanvas.studio"
              />
            </div>
            <div className="space-y-2.5">
              <Label className="overline text-white/60">Password</Label>
              <Input
                required
                type="password"
                data-testid="admin-password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="bg-black border-white/15 h-12 rounded-none text-white focus-visible:ring-1 focus-visible:ring-[#D4AF37] focus-visible:border-[#D4AF37]"
                placeholder="••••••••••"
              />
            </div>
            <button
              type="submit"
              disabled={busy}
              data-testid="admin-submit"
              className="group w-full inline-flex items-center justify-center gap-3 bg-[#D4AF37] text-black py-4 text-xs uppercase tracking-[0.22em] font-semibold hover:bg-[#e6c245] transition-all disabled:opacity-50"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Enter The Studio<ArrowRight className="w-4 h-4" /></>}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
